import * as d3 from 'd3';
import cx from 'classnames';
import i18next from 'i18next';

import { roundOneDecimal, THEME, typeHistogram } from '../../config/commons';

import { Dataset } from './interfaces';
import styles from './Histogram.module.scss';
import { colosPigmentation } from './histogram.helpers';

let isLightTheme: boolean;
const timeTransition = 300;

interface Props {
  container: HTMLDivElement | null;
  dataset: Dataset[];
  originalDataset?: Dataset[];
  histogram: string;
  limits: number[];
  average?: number;
  width: number;
  height: number;
  theme: THEME;
  unit?: string;
}

export default class HistogramD3 {
  container: HTMLDivElement | null;
  svg: d3.Selection<SVGGElement, unknown, null, undefined> = d3.select<SVGGElement, unknown>(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));

  x: d3.ScaleBand<string> = d3.scaleBand();
  y: d3.ScaleLinear<number, number, never> = d3.scaleLinear();
  xScaleLinear: d3.ScaleLinear<number, number, never> = d3.scaleLinear();

  bars: d3.Selection<SVGRectElement, Dataset, null, undefined> = d3.select<SVGRectElement, Dataset>(document.createElementNS('http://www.w3.org/2000/svg', 'rect'));
  textLimits: d3.Selection<SVGTextElement, number, null, unknown> = d3.select<SVGTextElement, number>(document.createElementNS('http://www.w3.org/2000/svg', 'text'));;
  textFrequencies: d3.Selection<SVGTextElement, Dataset, null, unknown> = d3.select<SVGTextElement, Dataset>(document.createElementNS('http://www.w3.org/2000/svg', 'text'));;

  width = 0;
  height = 0;
  margin = { top: 35, right: 12, bottom: 25, left: 12 };

  dataset: Dataset[];
  originalDataset: Dataset[];
  histogram: string;
  limits: number[];
  average?: number;
  unit?: string;

  prevBorderPositions: Map<number, { y1: number, y2: number }> = new Map();

  //styles
  averageTextStyle = '';
  limitsStyle = '';
  frequenciesStyles = '';
  xAxisPadding = 0.01;
  fillBars = '';
  borderLines = '';
  theme?: THEME;

  // eslint-disable-next-line
  constructor(props: Props) {
    const { average, container, dataset, originalDataset = [], height, histogram, limits, theme, width, unit } = props;
    this.container = container;
    this.width = width - this.margin.left - this.margin.right;
    this.height = height - this.margin.top - this.margin.bottom;

    this.dataset = dataset;
    this.originalDataset = originalDataset;
    this.histogram = histogram;
    this.limits = limits;
    this.average = average;
    this.unit = unit;

    this.theme = theme;

    this.updateStyles();
    this.createSvgElement(this.width, this.height);
    this.createHistogram();
  }

  updateStyles () {
    isLightTheme = this.theme === THEME.LIGHT;

    this.xAxisPadding = 0.01;
    this.fillBars = isLightTheme ? '#1b62e6' : '#0000ff';
    this.borderLines = isLightTheme ? styles.linesLight : styles.linesDark;
    this.averageTextStyle = styles.averageTextDefault;
    this.limitsStyle = isLightTheme ? styles.limitsDefaultLight : styles.limitsDefaultDark;
    this.frequenciesStyles = isLightTheme ? styles.frequenciesDefaultLight : styles.frequenciesDefaultDark;

  }

  createSvgElement (width: number, height: number) {
    const { container, margin } = this;
    d3.select(container).select('svg').remove();

    this.svg = d3.select(container)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .attr('id', 'content')
      .append('g')
      .attr('transform', `translate( ${margin.left} , ${margin.top} )`);
  }

  getFrecuencyPositionX (i: number) {
    return (i * this.x.bandwidth()) + (this.x.bandwidth() / 2) + (i * this.x.bandwidth() * this.xAxisPadding);
  }

  getFrecuencyLabel = (d: Dataset, i: number) => {
    const { dataset } = this;

    if (dataset.length >= 7) {
      if (d.percent) {
        return `<tspan dy='0em' x='${this.getFrecuencyPositionX(i)}'>${d.frequency}</tspan><tspan dy='1.0em' x='${this.getFrecuencyPositionX(i)}'>${roundOneDecimal(d.percent)}%</tspan>`;
      }
      return `${d.frequency}`;
    }

    if (d.percent) {
      return `${d.frequency} - ${roundOneDecimal(d.percent)}%`;
    }
    return `${d.frequency}`;
  }

  getFrecuencyPositionY = (d: Dataset) => {
    const { dataset } = this;

    if (dataset.length >= 7) {
      return this.y(d.frequency) - 15;
    }

    return this.y(d.frequency) - 5;
  }

  getHistogramBarClassname = (index: number) => {
    if (this.histogram === typeHistogram.PIGMENTATION) {
      return colosPigmentation[index];
    }

    return cx(styles.lines, this.borderLines);
  };
  
  renderHistogramBarBorders = () => {
    const { svg, height, originalDataset, dataset } = this;
  
    if (this.histogram === typeHistogram.PIGMENTATION || !originalDataset.length) {
      svg.selectAll('.originalHistogramGroup').remove();
      return;
    }
  
    const data = originalDataset.filter((d, i) => d.frequency !== dataset[i].frequency);
    const getX = (i: number) => this.x(i.toString()) as number;
  
    const groups = svg.selectAll<SVGGElement, Dataset>('.originalHistogramGroup')
      .data(data, (d) => d.index);
  
    groups.exit().remove();
  
    const groupsEnter = groups.enter()
      .append('g')
      .attr('class', 'originalHistogramGroup');
  
    // ENTER: always starts in height (new)
    groupsEnter.append('line').attr('class', 'border-left')
      .attr('x1', d => getX(d.index)).attr('x2', d => getX(d.index))
      .attr('y1', height).attr('y2', height);
  
    groupsEnter.append('line').attr('class', 'border-right')
      .attr('x1', d => getX(d.index) + this.x.bandwidth()).attr('x2', d => getX(d.index) + this.x.bandwidth())
      .attr('y1', height).attr('y2', height);
  
    groupsEnter.append('line').attr('class', 'border-top')
      .attr('x1', d => getX(d.index)).attr('x2', d => getX(d.index) + this.x.bandwidth())
      .attr('y1', height).attr('y2', height);
  
    // ENTER + UPDATE:
    const groupsMerge = groupsEnter.merge(groups);
  
    groupsMerge.select('.border-left')
      // Forces initial ATTR to the previous value if it exists
      .attr('y1', d => this.prevBorderPositions.get(d.index)?.y1 ?? height)
      .attr('y2', d => this.prevBorderPositions.get(d.index)?.y2 ?? height)
      .transition().duration(timeTransition)
      .attr('x1', d => getX(d.index))
      .attr('x2', d => getX(d.index))
      .attr('y1', d => this.y(d.frequency))
      .attr('y2', (_, i) => height - (height - this.y(dataset[i].frequency)))
      .attr('class', d => this.getHistogramBarClassname(d.index));
  
    groupsMerge.select('.border-right')
      .attr('y1', d => this.prevBorderPositions.get(d.index)?.y1 ?? height)
      .attr('y2', d => this.prevBorderPositions.get(d.index)?.y2 ?? height)
      .transition().duration(timeTransition)
      .attr('x1', d => getX(d.index) + this.x.bandwidth())
      .attr('x2', d => getX(d.index) + this.x.bandwidth())
      .attr('y1', d => this.y(d.frequency))
      .attr('y2', (_, i) => height - (height - this.y(dataset[i].frequency)))
      .attr('class', d => this.getHistogramBarClassname(d.index));
  
    groupsMerge.select('.border-top')
      .attr('y1', d => this.prevBorderPositions.get(d.index)?.y1 ?? height)
      .attr('y2', d => this.prevBorderPositions.get(d.index)?.y1 ?? height)
      .transition().duration(timeTransition)
      .attr('x1', d => getX(d.index))
      .attr('x2', d => getX(d.index) + this.x.bandwidth())
      .attr('y1', d => this.y(d.frequency))
      .attr('y2', d => this.y(d.frequency))
      .attr('class', d => this.getHistogramBarClassname(d.index));
  
    // Save new position for next rendering
    data.forEach((d, i) => {
      this.prevBorderPositions.set(
        d.index,
        {
          y1: this.y(d.frequency),
          y2: height - (height - this.y(dataset[i].frequency))
        }
      );
    });
  };
  
  createHistogram () {
    const { svg, height, dataset, limits, average } = this;

    this.buildAxisX();
    this.buildAxisY();

    // eslint-disable-next-line
    // @ts-ignore
    this.bars = svg.selectAll('.histogram')
      .data(dataset)
      .enter()
      .append('rect')
      .attr('class', 'histogram')
      .attr('x', (d: Dataset) => this.x(d.index.toString()) as number)
      .attr('width', this.x.bandwidth())
      .attr('y', (d: Dataset) => this.y(d.frequency))
      .attr('height', (d: Dataset) => height - this.y(d.frequency))
      .style('fill', (d: Dataset, index: number) => {
        if (this.histogram === typeHistogram.PIGMENTATION) {
          return colosPigmentation[index];
        }
        return this.fillBars;
      });

    this.renderHistogramBarBorders();

    const positionY = 16;
    // eslint-disable-next-line
    // @ts-ignore
    this.textLimits = svg.selectAll('.limits')
      .data(limits)
      .enter()
      .append('text')
      .attr('class', cx('limits', this.limitsStyle))
      .attr('x', (_, i: number) => (i * this.x.bandwidth()) + (i * this.x.bandwidth() * this.xAxisPadding))
      .attr('y', height + positionY)
      .style('text-anchor', 'middle')
      .text((d: number) => d);

    // eslint-disable-next-line
    // @ts-ignore
    this.textFrequencies = svg.selectAll('.frequencies')
      .data(dataset)
      .enter()
      .append('text')
      .attr('class', cx('frequencies', this.frequenciesStyles))
      .attr('x', (d: Dataset, i: number) => {
        return this.getFrecuencyPositionX(i);
      })
      .attr('y', (d: Dataset) => this.getFrecuencyPositionY(d))
      .style('text-anchor', 'middle')
      .html((d: Dataset, i: number) => this.getFrecuencyLabel(d, i));

    if (average && this.histogram !== typeHistogram.PIGMENTATION && this.histogram !== typeHistogram.SURVIVAL_RATE) {
      svg.append('line')
        .attr('class', cx('averageLine', styles.averageLine))
        .attr('x1', this.xScaleLinear(average))
        .attr('y1', -20)
        .attr('x2', this.xScaleLinear(average))
        .attr('y2', height);

      svg.append('text')
        .attr('class', cx('averageText', this.averageTextStyle))
        .attr('x', this.xScaleLinear(average))
        .attr('y', -25)
        .style('text-anchor', 'middle')
        .text(`${i18next.t('detail.avg')} ${average} ${this.unit}`);
    }
  }

  refreshHistogram (props: { dataset: Dataset[]; originalDataset?: Dataset[]; histogram: string; limits: number[]; average?: number; theme: THEME; unit?: string }) {
    const { average, dataset, originalDataset = [], histogram, limits, theme, unit } = props;
    const { container, svg, height } = this;

    this.dataset = dataset;
    this.originalDataset = originalDataset;
    this.histogram = histogram;
    this.limits = limits;
    this.average = average;
    this.theme = theme;
    this.unit = unit;

    this.updateStyles();
    this.buildAxisX();
    this.buildAxisY();

    // eslint-disable-next-line
    // @ts-ignore
    this.bars = svg.selectAll('.histogram')
      .data(dataset);

    this.bars.enter()
      .append('rect')
      .merge(this.bars)
      .transition()
      .duration(timeTransition)
      .attr('class', 'histogram')
      .attr('x', (d: Dataset) => this.x(d.index?.toString()) as number)
      .attr('width', this.x.bandwidth())
      .attr('y', (d: Dataset) => this.y(d.frequency))
      .attr('height', (d: Dataset) => height - this.y(d.frequency))
      .style('fill', (d: Dataset, index: number) => {
        if (this.histogram === typeHistogram.PIGMENTATION) {
          return colosPigmentation[index];
        }
        return this.fillBars;
      });

    this.bars.exit()
      .remove();

    this.renderHistogramBarBorders();

    const positionY = 16;

    //update limits
    // eslint-disable-next-line
    // @ts-ignore
    this.textLimits = svg.selectAll('.limits')
      .data(limits);

    this.textLimits.enter()
      .append('text')
      .merge(this.textLimits)
      .transition()
      .duration(0)
      .attr('class', cx('limits', this.limitsStyle))
      .attr('x', (d: number, i: number) => (i * this.x.bandwidth()) + (i * this.x.bandwidth() * this.xAxisPadding))
      .attr('y', height + positionY)
      .style('text-anchor', 'middle')
      .text((d: number) => d);

    this.textLimits.exit()
      .remove();

    //update frequency
    // eslint-disable-next-line
    // @ts-ignore
    this.textFrequencies = svg.selectAll('.frequencies')
      .data(dataset);

    this.textFrequencies.enter()
      .append('text')
      .merge(this.textFrequencies)
      .attr('class', cx('frequencies', this.frequenciesStyles))
      .attr('x', (d: Dataset, i: number) => this.getFrecuencyPositionX(i))
      .attr('y', (d: Dataset) => this.getFrecuencyPositionY(d))
      .style('text-anchor', 'middle')
      .html((d: Dataset, i: number) => this.getFrecuencyLabel(d, i));

    this.textFrequencies.exit()
      .remove();

    d3.select(container).selectAll('.averageLine').remove();
    d3.select(container).selectAll('.averageText').remove();

    if (average && this.histogram !== typeHistogram.PIGMENTATION && this.histogram !== typeHistogram.SURVIVAL_RATE) {
      svg.append('line')
        .attr('class', cx('averageLine', styles.averageLine))
        .attr('x1', this.xScaleLinear(average))
        .attr('y1', -20)
        .attr('x2', this.xScaleLinear(average))
        .attr('y2', height);

      svg.append('text')
        .attr('class', cx('averageText', this.averageTextStyle))
        .attr('x', this.xScaleLinear(average))
        .attr('y', -25)
        .style('text-anchor', 'middle')
        .text(`${i18next.t('detail.avg')} ${average} ${this.unit}`);
    }
  }

  buildAxisX () {
    const { dataset, limits, width } = this;

    this.x = d3.scaleBand()
      .domain(dataset.map(function (d) { return d.index.toString(); }))
      .range([0, width])
      .padding(this.xAxisPadding);

    this.xScaleLinear = d3.scaleLinear()
      .domain([limits[0], limits[limits.length - 1]])
      .range([0, width]);
  }

  buildAxisY () {
    const { dataset, originalDataset, height } = this;
    const frequencies = [...dataset.map((item) => item.frequency), ...originalDataset.map((item) => item.frequency)];
    let maxFrequency = Math.max(...frequencies);
    maxFrequency = maxFrequency <= 0 ? 10 : maxFrequency;

    this.y = d3
      .scaleLinear()
      .domain([0, maxFrequency])
      .range([height, 0]);
  }

  resize (width: number, height: number) {
    const { container } = this;
    this.width = width - this.margin.left - this.margin.right;
    this.height = height - this.margin.top - this.margin.bottom;

    d3.select(container).select('#content').selectAll('*').remove();

    this.createSvgElement(this.width, this.height);
    this.createHistogram();
  }
}
